// src/views/proprio/_ganttGeometry.js — G6 (proprio lane-Gantt), géométrie PURE.
//
// Positionnement date→colonne / packing d'une grille lane-Gantt : 1 ligne =
// 1 logement, 1 colonne = 1 jour du mois, barres de séjour continues.
//
// Logique COPIÉE (pas importée) du `bars` useMemo de src/tabs/calendar/TableauView.jsx
// (~L237-262, chemin argent GELÉ — jamais touché ici) :
//   - convention chambre d'hôtel : start_date INCLUS → end_date EXCLUS
//     (endLastIncluded = end_date − 1 jour) ;
//   - clip aux bornes du mois affiché ;
//   - une ligne par logement (pas de lane-packing multi-lignes : une chambre ne
//     peut pas être double-bookée sur les mêmes nuits).
// Ici on l'exprime en COMPARAISON DE CHAÎNES ISO (YYYY-MM-DD) — déterministe et
// indépendant du fuseau (même parti-pris que _grid-geometry.js), là où TableauView
// manipule des objets Date+setHours(0,0,0,0) (OK en front local, fragile en CI).
//
// Aucune dépendance React/DOM/réseau/métier : que des chaînes et des entiers.

// ISO local (YYYY-MM-DD) depuis les composantes locales d'une Date. Identique à
// isoOf de ProprioCalendrierView / _grid-geometry (pas d'Intl/TZ).
export function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Jours du mois (1..dernier) en ISO local, dans l'ordre. year=YYYY, month1=1..12.
export function monthDaysIso(year, month1) {
  const last = new Date(year, month1, 0).getDate()
  const out = []
  for (let day = 1; day <= last; day++) out.push(isoOf(new Date(year, month1 - 1, day)))
  return out
}

// Barres de séjour positionnées sur la grille du mois.
//   items       : [{ logement_id, start_date, end_date, ... }] (ISO YYYY-MM-DD)
//   daysIso     : ISO des jours du mois (monthDaysIso) — colonnes 0..N-1
//   logIdToRow  : Map|obj logement_id → index de ligne (0-based)
// Retour : [{ item, rowIdx, startCol, endCol }] (startCol ≤ endCol, dans [0,N-1]).
// Un item qui ne touche pas le mois (ou logement absent, ou dates vides/inversées)
// est ignoré. La colonne i (jour `iso`) est COUVERTE ⇔ start ≤ iso < end (fin
// exclusive = convention chambre d'hôtel) → clip au mois automatique (les colonnes
// ne couvrent QUE le mois affiché).
export function computeMonthBars(items, daysIso, logIdToRow) {
  const getRow = (id) => (logIdToRow instanceof Map ? logIdToRow.get(id) : logIdToRow?.[id])
  const N = Array.isArray(daysIso) ? daysIso.length : 0
  const out = []
  for (const it of items || []) {
    if (!it || it.logement_id == null) continue
    const rowIdx = getRow(it.logement_id)
    if (rowIdx == null) continue
    const s = it.start_date
    const e = it.end_date
    if (!s || !e || e <= s) continue
    let startCol = -1
    let endCol = -1
    for (let i = 0; i < N; i++) {
      const iso = daysIso[i]
      if (s <= iso && iso < e) {
        if (startCol === -1) startCol = i
        endCol = i
      }
    }
    if (startCol === -1) continue // séjour hors du mois affiché
    out.push({ item: it, rowIdx, startCol, endCol })
  }
  return out
}

// Ensemble des cellules OCCUPÉES (logId → Set<dateIso>) par une liste d'items,
// pour supprimer l'affichage du prix sous une barre (garde « bug 34 » de
// TableauView : pas de prix qui déborde d'une barre). Même règle de couverture.
export function buildCoveredSet(items, daysIso) {
  const daySet = new Set(Array.isArray(daysIso) ? daysIso : [])
  const map = new Map()
  for (const it of items || []) {
    if (!it || it.logement_id == null || !it.start_date || !it.end_date) continue
    if (it.end_date <= it.start_date) continue
    let inner = map.get(it.logement_id)
    if (!inner) { inner = new Set(); map.set(it.logement_id, inner) }
    for (const iso of daySet) {
      if (it.start_date <= iso && iso < it.end_date) inner.add(iso)
    }
  }
  return map
}

// Index prix : logId → Map<dateIso, { price1, min_stay }> depuis les lignes
// daily_pricing renvoyées par l'endpoint proprio_pricing_calendar. Lecture pure.
export function buildPricingIndex(pricingRows) {
  const map = new Map()
  for (const r of pricingRows || []) {
    if (!r || r.logement_id == null || !r.date) continue
    let inner = map.get(r.logement_id)
    if (!inner) { inner = new Map(); map.set(r.logement_id, inner) }
    inner.set(r.date, { price1: r.price1, min_stay: r.min_stay })
  }
  return map
}
